import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Calendar as CalendarIcon, Clock, Search, ChevronLeft, ChevronRight, Eye, EyeOff, Grid } from "lucide-react";
import { useTimeSlots, TimeSlot } from "@/hooks/useTimeSlots";
import { Service } from "@/hooks/useServices";
import { Room } from "@/hooks/useRooms";
import AddTimeSlotDialog from "./AddTimeSlotDialog";
import BulkAddTimeSlotsDialog from "./BulkAddTimeSlotsDialog";
import BulkDeleteTimeSlotsDialog from "./BulkDeleteTimeSlotsDialog";
import TimeSlotTableRow from "./TimeSlotTableRow";
import EditTimeSlotDialog from "./EditTimeSlotDialog";
import AvailabilityCalendar from "./AvailabilityCalendar";
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks, startOfDay, endOfDay, isSameDay } from "date-fns";
import { cn } from "@/lib/utils";

interface TimeSlotManagementProps {
  providerId: string;
  services: Service[];
  rooms: Room[];
}

const TimeSlotManagement = ({ providerId, services, rooms }: TimeSlotManagementProps) => {
  const [selectedService, setSelectedService] = useState<string>("all");
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingTimeSlot, setEditingTimeSlot] = useState<TimeSlot | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showPastSlots, setShowPastSlots] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");

  // Calculate date range for database filtering
  const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 1 });
  const startDateStr = format(weekStart, 'yyyy-MM-dd') + 'T00:00:00Z';
  const endDateStr = format(weekEnd, 'yyyy-MM-dd') + 'T23:59:59Z';
  
  const { data: timeSlots, isLoading, refetch } = useTimeSlots(
    providerId, 
    selectedService === "all" ? undefined : selectedService,
    startDateStr,
    endDateStr
  );

  const now = new Date();

  const filteredTimeSlots = timeSlots?.filter(slot => {
    const slotDate = new Date(slot.start_time);
    
    // If a specific date is selected from calendar, filter by that date
    const matchesDate = selectedDate ? isSameDay(slotDate, selectedDate) : true;
    
    const matchesSearch = searchQuery === "" || 
      slot.service?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      slot.room?.name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter out past slots unless showPastSlots is true
    const isPast = slotDate < now;
    const matchesTimeFilter = showPastSlots || !isPast;
    
    return matchesDate && matchesSearch && matchesTimeFilter;
  }) || [];

  const handleTimeSlotAdded = () => {
    refetch();
  };

  const handleEditTimeSlot = (timeSlot: TimeSlot) => {
    setEditingTimeSlot(timeSlot);
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingTimeSlot(null);
  };

  const handlePreviousWeek = () => {
    setSelectedWeek(prev => subWeeks(prev, 1));
    setSelectedDate(null); // Clear specific date selection when navigating weeks
  };

  const handleNextWeek = () => {
    setSelectedWeek(prev => addWeeks(prev, 1));
    setSelectedDate(null); // Clear specific date selection when navigating weeks
  };

  const handleThisWeek = () => {
    setSelectedWeek(new Date());
    setSelectedDate(null); // Clear specific date selection
  };

  const handleDateSelect = (date: Date | undefined) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const clearDateFilter = () => {
    setSelectedDate(null);
  };

  const getDisplayDateRange = () => {
    if (selectedDate) {
      return format(selectedDate, "EEEE, MMM dd, yyyy");
    }
    return `${format(weekStart, "MMM dd")} - ${format(weekEnd, "MMM dd, yyyy")}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Time Slot Management</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="text-lg sm:text-xl">Time Slot Management</CardTitle>
            <div className="flex flex-col sm:flex-row gap-2">
              <BulkAddTimeSlotsDialog
                providerId={providerId}
                services={services}
                rooms={rooms}
                onTimeSlotsAdded={handleTimeSlotAdded}
              />
              <BulkDeleteTimeSlotsDialog
                providerId={providerId}
                services={services}
                rooms={rooms}
                onTimeSlotsDeleted={handleTimeSlotAdded}
              />
              <AddTimeSlotDialog
                providerId={providerId}
                services={services}
                rooms={rooms}
                onTimeSlotAdded={handleTimeSlotAdded}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          {/* View Mode Toggle */}
          <div className="flex items-center justify-center sm:justify-start">
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <Button
                variant={viewMode === "table" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("table")}
                className="h-8 px-3 text-xs sm:text-sm"
              >
                <Clock className="h-4 w-4 mr-1" />
                Table View
              </Button>
              <Button
                variant={viewMode === "calendar" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("calendar")}
                className="h-8 px-3 text-xs sm:text-sm"
              >
                <Grid className="h-4 w-4 mr-1" />
                Calendar View
              </Button>
            </div>
          </div>

          {viewMode === "calendar" ? (
            <div className="space-y-4">
              {/* Service Filter for Calendar View */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Availability Calendar */}
              <AvailabilityCalendar 
                providerId={providerId}
                services={services}
                selectedService={selectedService}
              />
            </div>
          ) : (
            <>
              {/* Week Navigation with Calendar Picker - Table View Only */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                {/* ... keep existing code (week navigation) */}
                <div className="flex items-center justify-center sm:justify-start">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handlePreviousWeek}
                    className="rounded-r-none px-2 sm:px-3"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline ml-1">Previous</span>
                  </Button>
                  
                  <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "flex items-center space-x-2 px-3 py-2 bg-gray-50 border-t border-b border-gray-300 rounded-none font-medium text-sm sm:text-base whitespace-nowrap",
                          "hover:bg-gray-100",
                          selectedDate && "bg-blue-50 border-blue-300"
                        )}
                      >
                        <CalendarIcon className="h-4 w-4 text-gray-500" />
                        <span>
                          {getDisplayDateRange()}
                        </span>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="center">
                      <Calendar
                        mode="single"
                        selected={selectedDate || selectedWeek}
                        onSelect={handleDateSelect}
                        initialFocus
                        className="pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleNextWeek}
                    className="rounded-l-none px-2 sm:px-3"
                  >
                    <span className="hidden sm:inline mr-1">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex gap-2">
                  {selectedDate && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={clearDateFilter}
                      className="text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      Show Week View
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleThisWeek}
                    className="w-full sm:w-auto"
                  >
                    This Week
                  </Button>
                </div>
              </div>

              {/* Search, Service Filter, and Past Slots Toggle - Table View Only */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                {/* ... keep existing code (search and filters) */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search time slots..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={selectedService} onValueChange={setSelectedService}>
                  <SelectTrigger className="w-full sm:w-48">
                    <SelectValue placeholder="Filter by service" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Services</SelectItem>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.id}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPastSlots(!showPastSlots)}
                  className={cn(
                    "w-full sm:w-auto whitespace-nowrap",
                    showPastSlots && "bg-gray-100"
                  )}
                >
                  {showPastSlots ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Hide Past Slots
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Show Past Slots
                    </>
                  )}
                </Button>
              </div>

              {/* Time Slots Table */}
              {filteredTimeSlots.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="min-w-[120px]">Date</TableHead>
                          <TableHead className="min-w-[120px]">Time</TableHead>
                          <TableHead className="min-w-[120px]">Service</TableHead>
                          <TableHead className="min-w-[100px]">Room</TableHead>
                          <TableHead className="min-w-[100px]">Bookings</TableHead>
                          <TableHead className="min-w-[100px]">Status</TableHead>
                          <TableHead className="min-w-[140px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredTimeSlots.map((timeSlot) => (
                          <TimeSlotTableRow
                            key={timeSlot.id}
                            timeSlot={timeSlot}
                            onEdit={handleEditTimeSlot}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p className="font-medium">
                    {selectedDate 
                      ? `No time slots found for ${format(selectedDate, "MMMM dd, yyyy")}`
                      : "No time slots found for the selected week"
                    }
                  </p>
                  <p className="text-sm mt-2">
                    {selectedDate 
                      ? "Try selecting a different date or switch to week view"
                      : showPastSlots 
                        ? "Create your first time slot to get started"
                        : "Create your first time slot to get started, or toggle 'Show Past Slots' to see previous time slots"
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Time Slot Dialog */}
      <EditTimeSlotDialog
        timeSlot={editingTimeSlot}
        open={editDialogOpen}
        onOpenChange={handleEditDialogClose}
      />
    </>
  );
};

export default TimeSlotManagement;
